import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { CreditCard, MessageCircle, Shield, RotateCcw, CheckCircle2, Sparkles, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import CouponBox from './CouponBox';
import AddressFields, { EMPTY_ADDRESS, isAddressValid, formatAddress } from './AddressFields';
import { ensureRazorpayClickable } from '../lib/razorpayUnblock';
import { trackFormSubmit, gtmLeadSubmit, gtmBeginCheckout, gtmPurchase } from '../lib/tracker';
import PremisesAreaSelector, { MAX_AREA_SQFT } from './PremisesAreaSelector';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BHK_OPTIONS = [
  { value: '1bhk', label: '1 BHK', area: 500 },
  { value: '2bhk', label: '2 BHK', area: 800 },
  { value: '3bhk', label: '3 BHK', area: 1200 },
  { value: '4bhk', label: '4 BHK', area: 1600 },
];

/**
 * Service-specific calculator. Pest type is fixed by the service slug, so users
 * only see inputs relevant to that service. Calculation logic mirrors the global
 * calculator (area × rate, floored at minPrice, rounded to ₹100) so pricing
 * stays consistent across the site.
 */
export default function ServiceCalculator({ service, config, variant = 'sidebar', areaLimit = MAX_AREA_SQFT }) {
  const [bhk, setBhk] = useState('');
  const [manualArea, setManualArea] = useState('');
  const [hive, setHive] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [notes, setNotes] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquirySuccess, setEnquirySuccess] = useState(false);
  const priceEligibleAreaLimit = Math.max(1, Math.min(MAX_AREA_SQFT, Number(areaLimit) || MAX_AREA_SQFT));

  // ---------- Coupon state ----------
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount, final_amount, original_amount, message }
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // ---------- Real-time price calculation ----------
  // Each service has a `minPrice` which acts as its BASE PRICE. We display
  // the base price the moment the user lands (so they always see something,
  // never a flat "₹0"). Once a property size is selected, we compute the real
  // quote (area × rate, floored at minPrice). For per-sqft services that
  // have no minPrice, we still show ₹0 until size is entered.
  const calculatedPrice = useMemo(() => {
    if (!config) return 0;
    if (config.pricingModel === 'flat-by-hive') {
      const opt = (config.hiveOptions || []).find((o) => o.value === hive);
      if (opt) return opt.price;
      // Show smallest hive price as base
      const baseHive = (config.hiveOptions || [])[0];
      return baseHive ? baseHive.price : 0;
    }
    const area = manualArea
      ? parseInt(manualArea, 10)
      : BHK_OPTIONS.find((b) => b.value === bhk)?.area || 0;
    if (area > priceEligibleAreaLimit) return 0;
    if (!area || area <= 0) {
      // No size chosen yet → show base price (= minPrice) so the page is never blank.
      return config.minPrice || 0;
    }
    let price = area * (config.rate || 0);
    if (price < (config.minPrice || 0)) price = config.minPrice || 0;
    if (config.pricingModel === 'tier') price = Math.round(price / 100) * 100;
    else price = Math.round(price);
    return price;
  }, [bhk, manualArea, hive, config, priceEligibleAreaLimit]);

  const selectedArea = manualArea ? parseInt(manualArea, 10) : (BHK_OPTIONS.find((b) => b.value === bhk)?.area || 0);
  const isAreaOverLimit = config?.pricingModel !== 'flat-by-hive' && selectedArea > priceEligibleAreaLimit;

  // Has the user actually picked a size? Used to label the price as "Base / Starting"
  // when nothing is selected.
  const sizeSelected = useMemo(() => {
    if (!config) return false;
    if (config.pricingModel === 'flat-by-hive') return !!hive;
    return !!(bhk || (manualArea && parseInt(manualArea, 10) > 0));
  }, [bhk, manualArea, hive, config]);

  // animate displayed price
  const [displayPrice, setDisplayPrice] = useState(0);
  useEffect(() => {
    if (calculatedPrice === displayPrice) return;
    const start = typeof displayPrice === 'number' ? displayPrice : 0;
    const diff = calculatedPrice - start;
    if (diff === 0) return;
    const steps = 18;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setDisplayPrice(Math.round(start + (diff * i) / steps));
      if (i >= steps) {
        setDisplayPrice(calculatedPrice);
        clearInterval(id);
      }
    }, 22);
    return () => clearInterval(id);
  }, [calculatedPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-clear applied coupon if base price changes — user must re-apply
  useEffect(() => {
    if (appliedCoupon && appliedCoupon.original_amount !== calculatedPrice) {
      setAppliedCoupon(null);
      setCouponError('');
    }
  }, [calculatedPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  const finalAmount = appliedCoupon ? appliedCoupon.final_amount : calculatedPrice;
  const discountAmount = appliedCoupon ? appliedCoupon.discount : 0;

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    setCouponError('');
    if (!code) { setCouponError('Please enter a coupon code'); return; }
    if (calculatedPrice <= 0) { setCouponError('Select property size first to see the price'); return; }
    setCouponLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/coupons/validate`, {
        code,
        amount: calculatedPrice,
        service_slug: service.slug,
        phone: phone || null,
      }, { timeout: 6000 });
      if (res.data?.valid) {
        setAppliedCoupon({
          code: res.data.code,
          discount: res.data.discount,
          final_amount: res.data.final_amount,
          original_amount: res.data.original_amount,
          message: res.data.message,
        });
        setCouponError('');
      } else {
        setAppliedCoupon(null);
        setCouponError(res.data?.message || 'Invalid coupon');
      }
    } catch (e) {
      setAppliedCoupon(null);
      setCouponError(e?.response?.data?.detail || 'Could not validate coupon. Try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const isFormValid = () => {
    const sizeOk = config.pricingModel === 'flat-by-hive'
      ? !!hive
      : !!(bhk || (manualArea && parseInt(manualArea, 10) > 0));
    return !!(name && phone && phone.length >= 10 && city && sizeOk && isAddressValid(address));
  };

  const handleAreaChange = (value) => {
    const numeric = Number(value);
    if (!Number.isInteger(numeric) || numeric < 0 || numeric > MAX_AREA_SQFT) {
      setManualArea('');
      return;
    }
    setManualArea(numeric > 0 ? String(numeric) : '');
    if (numeric > 0) setBhk('');
  };

  const propertySizeText = () => {
    if (config.pricingModel === 'flat-by-hive') {
      const opt = (config.hiveOptions || []).find((o) => o.value === hive);
      return opt ? opt.label : '';
    }
    if (manualArea) return `${manualArea} sq ft`;
    return BHK_OPTIONS.find((b) => b.value === bhk)?.label || '';
  };

  const buildLeadPayload = () => ({
    name,
    phone,
    email: null,
    city,
    pest_type: config.pestKey,
    pest_label: config.pestLabel,
    property_size: propertySizeText(),
    area_sqft: manualArea
      ? parseInt(manualArea, 10)
      : BHK_OPTIONS.find((b) => b.value === bhk)?.area || null,
    estimated_price: isAreaOverLimit ? 'Quote on Call' : calculatedPrice,
    additional_details: notes || null,
    source: `pricing-page:${service.slug}`,
    address: {
      state: address.state || '',
      district: address.district || '',
      city: address.city || city || '',
      line1: address.line1 || '',
      pincode: address.pincode || '',
      landmark: address.landmark || '',
    },
  });

  const persistLead = async () => {
    try {
      const payload = buildLeadPayload();
      const res = await axios.post(`${API_BASE}/leads`, payload, { timeout: 5000 });
      const leadId = res.data?.id || null;
      trackFormSubmit({ source: 'pricing_page', service: service.slug, has_coupon: !!appliedCoupon });
      gtmLeadSubmit({
        leadId,
        segment: 'residential',
        pestType: payload.pest_type,
        pestLabel: payload.pest_label,
        areaSqft: payload.area_sqft,
        value: typeof payload.estimated_price === 'number' ? payload.estimated_price : 0,
        city: payload.city,
        source: payload.source,
      });
      return leadId;
    } catch (e) {
      console.warn('Lead save failed', e?.message);
      return null;
    }
  };

  const handleClear = () => {
    setBhk('');
    setManualArea('');
    setHive('');
    setName('');
    setPhone('');
    setCity('');
    setAddress(EMPTY_ADDRESS);
    setNotes('');
    setCouponInput('');
    setAppliedCoupon(null);
    setCouponError('');
    setEnquirySuccess(false);
  };

  // Submit Enquiry — lighter than booking. Only requires name + phone + city.
  // Persists as a lead with source="pricing-page-enquiry:<slug>" so admin can
  // distinguish in the Leads tab.
  const isEnquiryValid = () => !!(name && phone && phone.length >= 10 && city);

  const handleSubmitEnquiry = async () => {
    if (!isEnquiryValid() || enquirySubmitting) return;
    setEnquirySubmitting(true);
    try {
      const payload = {
        ...buildLeadPayload(),
        source: `pricing-page-enquiry:${service.slug}`,
      };
      const res = await axios.post(`${API_BASE}/leads`, payload, { timeout: 8000 });
      const leadId = res?.data?.id || null;
      trackFormSubmit({ source: 'pricing_page_enquiry', service: service.slug });
      gtmLeadSubmit({
        leadId,
        segment: 'residential',
        pestType: config.pestKey,
        pestLabel: config.pestLabel,
        areaSqft: payload.area_sqft,
        value: typeof payload.estimated_price === 'number' ? payload.estimated_price : 0,
        city: payload.city,
        source: payload.source,
      });
      setEnquirySuccess(true);
    } catch (e) {
      console.warn('Enquiry submit failed', e?.message);
      alert(e?.response?.data?.detail || 'Could not submit your enquiry. Please try WhatsApp instead.');
    } finally {
      setEnquirySubmitting(false);
    }
  };

  const handleWhatsApp = async () => {
    if (!isFormValid()) return;
    setIsRedirecting(true);
    await persistLead();
    const priceText = calculatedPrice > 0 ? `₹${calculatedPrice}` : 'On Inspection';
    const message = `🌟 New Pest Control Inquiry — ${config.pestLabel}

Name: ${name}
Phone: ${phone}
City: ${city}
Address: ${formatAddress(address) || '—'}

Service: ${service.name}
Property: ${propertySizeText()}
Estimated Price: ${priceText}${notes ? `\n\nDetails:\n${notes}` : ''}`;
    window.open(`https://api.whatsapp.com/send?phone=919410110910&text=${encodeURIComponent(message)}`, '_blank');
    setTimeout(() => setIsRedirecting(false), 800);
  };

  const handlePayNow = async () => {
    if (!isFormValid() || finalAmount <= 0) return;
    if (typeof window.Razorpay !== 'function') {
      alert('Payment library is still loading. Please retry in a moment.');
      return;
    }
    setIsProcessingPayment(true);
    const leadId = await persistLead();

    // GA4 begin_checkout
    gtmBeginCheckout({
      leadId,
      segment: 'residential',
      pestType: config.pestKey,
      pestLabel: config.pestLabel,
      value: calculatedPrice,
      finalValue: finalAmount,
      coupon: appliedCoupon?.code || null,
    });

    let order;
    try {
      const res = await axios.post(`${API_BASE}/payments/create-order`, {
        amount: calculatedPrice,  // original — backend recomputes with coupon
        name,
        phone,
        email: null,
        lead_id: leadId,
        coupon_code: appliedCoupon?.code || null,
        service_slug: service.slug,
        notes: { city, pest_type: config.pestKey, service: service.slug },
      }, { timeout: 10000 });
      order = res.data;
    } catch (err) {
      console.error('Create order failed', err);
      const msg = err?.response?.data?.detail;
      // If coupon became invalid server-side, surface it
      if (msg && typeof msg === 'string' && /coupon|discount|min/i.test(msg)) {
        setCouponError(msg);
        setAppliedCoupon(null);
      } else {
        alert(msg || 'Could not start payment. Please try WhatsApp instead.');
      }
      setIsProcessingPayment(false);
      return;
    }

    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency || 'INR',
      name: 'EntoRid Pest Control',
      description: `${service.name} — ${propertySizeText()}`,
      order_id: order.order_id,
      prefill: { name, contact: phone },
      notes: { city, lead_id: leadId || '', service: service.slug },
      theme: { color: '#00A86B' },
      modal: {
        ondismiss: () => setIsProcessingPayment(false),
        confirm_close: true,
      },
      handler: async (response) => {
        try {
          await axios.post(`${API_BASE}/payments/verify`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            lead_id: leadId,
            email: null,
          });
          setPaymentSuccess(true);
          gtmPurchase({
            transactionId: response.razorpay_payment_id,
            leadId,
            segment: 'residential',
            pestType: config.pestKey,
            pestLabel: config.pestLabel,
            value: calculatedPrice,
            finalValue: finalAmount,
            coupon: appliedCoupon?.code || null,
            discount: discountAmount,
          });
          const thanksMsg = `Hi EntoRid! 👋\nMy payment of ₹${finalAmount} for ${service.name} is successful.${appliedCoupon ? `\nCoupon Applied: ${appliedCoupon.code} (saved ₹${discountAmount})` : ''}\n\nName: ${name}\nPhone: ${phone}\nCity: ${city}\nProperty: ${propertySizeText()}\n\nPlease confirm my booking schedule. Thank you!`;
          window.open(`https://api.whatsapp.com/send?phone=919410110910&text=${encodeURIComponent(thanksMsg)}`, '_blank');
        } catch (err) {
          console.error('Verify failed', err);
          alert('Payment received but verification failed. We will contact you shortly.');
        } finally {
          setIsProcessingPayment(false);
        }
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      // Defensive: ensure Razorpay checkout is touch-clickable on mobile even
      // if some modal lib applies aria-hidden / pointer-events:none on it.
      const stopUnblock = ensureRazorpayClickable();
      const origDismiss = options.modal.ondismiss;
      options.modal.ondismiss = () => {
        try { stopUnblock(); } catch (_) { /* noop */ }
        if (origDismiss) origDismiss();
      };
      rzp.on('payment.failed', () => {
        try { stopUnblock(); } catch (_) { /* noop */ }
        setIsProcessingPayment(false);
      });
      rzp.open();
    } catch (err) {
      console.error('Razorpay open failed', err);
      setIsProcessingPayment(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl text-center shadow-xl" data-testid="svc-calc-success">
        <CheckCircle2 className="mx-auto mb-2" size={40} />
        <div className="text-lg font-bold">Payment Successful!</div>
        <div className="text-sm text-green-50 mt-1">We&apos;ll contact you shortly to schedule the service.</div>
      </div>
    );
  }

  const priceLabel = isAreaOverLimit ? 'Custom Quote' : calculatedPrice > 0
    ? `₹${calculatedPrice.toLocaleString()}`
    : 'Select details';
  const priceHint = calculatedPrice > 0 && !sizeSelected
    ? 'Starting from'
    : null;

  return (
    <div
      className={`bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-gray-100 overflow-hidden ${variant === 'sheet' ? '' : ''}`}
      data-testid="service-calculator"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="text-3xl" aria-hidden>{config.icon}</div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-green-100 font-semibold">Instant Quote</div>
            <h3 className="text-base sm:text-lg font-bold leading-tight">{config.pestLabel}</h3>
          </div>
        </div>
        <div className="mt-3 bg-white/15 backdrop-blur rounded-xl p-3 border border-white/20">
          <div className="text-[10px] uppercase tracking-wide text-green-100 font-semibold flex items-center gap-1.5">
            <span>Estimated Cost</span>
            {priceHint && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-white/25 text-[9px] font-bold tracking-wider">
                {priceHint}
              </span>
            )}
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="svc-calc-price">
            {priceLabel}
          </div>
          {isAreaOverLimit ? <div className="mt-1 text-[11px] text-green-50">Contact our sales team for the best price above {priceEligibleAreaLimit.toLocaleString('en-IN')} sq. ft.</div> : calculatedPrice > 0 && (
            <div className="text-[11px] text-green-100 mt-0.5 flex items-center gap-1">
              <Sparkles size={11} /> {sizeSelected ? 'Final price may vary after inspection' : 'Pick a property size for an accurate quote'}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-5 space-y-5">
        {/* Submit-Enquiry success banner — sits at the very top of the body */}
        {enquirySuccess && (
          <div
            className="rounded-xl bg-green-50 border-2 border-green-300 p-3 flex items-start gap-2"
            data-testid="svc-calc-enquiry-success"
          >
            <CheckCircle2 className="text-green-600 mt-0.5 flex-shrink-0" size={18} />
            <div>
              <div className="text-sm font-bold text-green-800">Enquiry received!</div>
              <div className="text-xs text-green-700 mt-0.5">
                Our team will call you on <span className="font-semibold">{phone}</span> within 30 minutes.
                You can also book online or chat on WhatsApp below.
              </div>
            </div>
          </div>
        )}

        {/* Coupon Section — sits right below the price/header card */}
        {!isAreaOverLimit && <CouponBox
          couponInput={couponInput}
          setCouponInput={(v) => { setCouponInput(v); if (couponError) setCouponError(''); }}
          appliedCoupon={appliedCoupon}
          couponError={couponError}
          couponLoading={couponLoading}
          baseAmount={calculatedPrice}
          onApply={handleApplyCoupon}
          onRemove={handleRemoveCoupon}
        />}

        {/* Property Size */}
        {config.showBhk && (
          <div>
            <Label className="text-sm font-semibold text-gray-800">Property Size</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {BHK_OPTIONS.map((b) => (
                <button
                  key={b.value}
                  type="button"
                  onClick={() => { setBhk(b.value); setManualArea(''); }}
                  data-testid={`svc-calc-bhk-${b.value}`}
                  className={`p-3 rounded-xl border-2 text-left transition-all min-h-[60px] ${
                    bhk === b.value
                      ? 'border-green-600 bg-green-50 shadow-sm'
                      : 'border-gray-200 hover:border-green-300 active:scale-[0.98]'
                  }`}
                >
                  <div className={`font-semibold text-sm ${bhk === b.value ? 'text-green-700' : 'text-gray-900'}`}>{b.label}</div>
                  <div className="text-[11px] text-gray-500">{b.area} sq ft</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {config.showCustomArea && (
          <div>
            {config.showBhk && <div className="text-center text-[11px] text-gray-400 font-medium uppercase tracking-wider mb-2">— or —</div>}
            <PremisesAreaSelector value={manualArea} onChange={handleAreaChange} label={config.customAreaLabel || 'Custom Area'} maxArea={MAX_AREA_SQFT} testId="svc-calc-area-selector" />
            <p className="mt-2 text-xs text-slate-500" data-testid="svc-calc-area-limit">Instant pricing up to {priceEligibleAreaLimit.toLocaleString('en-IN')} sq. ft. Larger sites receive a tailored sales quote.</p>
          </div>
        )}

        {config.pricingModel === 'flat-by-hive' && (
          <div>
            <Label className="text-sm font-semibold text-gray-800">Hive Size</Label>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {(config.hiveOptions || []).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setHive(opt.value)}
                  data-testid={`svc-calc-hive-${opt.value}`}
                  className={`p-3 rounded-xl border-2 text-left transition-all flex items-center justify-between min-h-[56px] ${
                    hive === opt.value
                      ? 'border-green-600 bg-green-50 shadow-sm'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div>
                    <div className={`font-semibold text-sm ${hive === opt.value ? 'text-green-700' : 'text-gray-900'}`}>{opt.label}</div>
                    <div className="text-[11px] text-gray-500">{opt.desc}</div>
                  </div>
                  <div className="text-sm font-bold text-green-600">₹{opt.price.toLocaleString()}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Contact */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-gray-800">Your Details</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full Name"
            className="h-11"
            data-testid="svc-calc-name"
          />
          <Input
            type="tel"
            inputMode="numeric"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="10-digit Phone"
            className="h-11"
            maxLength={10}
            data-testid="svc-calc-phone"
          />
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City (e.g., Dehradun)"
            className="h-11"
            data-testid="svc-calc-city"
          />
          <div className="pt-1">
            <AddressFields address={address} setAddress={setAddress} idPrefix="svc-addr" compact />
          </div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Specific requirements (optional)"
            rows={2}
            className="resize-none"
            data-testid="svc-calc-notes"
          />
        </div>

        {/* Buttons */}
        <div className="space-y-2 pt-1">
          {isAreaOverLimit ? <div className="grid grid-cols-2 gap-2" data-testid="svc-calc-area-limit-actions">
            <a href="tel:+917819988010" className="inline-flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl bg-amber-700 px-3 text-sm font-bold text-white transition-colors hover:bg-amber-800" data-testid="svc-calc-area-limit-call"><CreditCard size={16} /> Call Sales</a>
            <a href="https://api.whatsapp.com/send?phone=919410110910" target="_blank" rel="noreferrer" className="inline-flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl border border-green-600 bg-white px-3 text-sm font-bold text-green-700 transition-colors hover:bg-green-50" data-testid="svc-calc-area-limit-whatsapp"><MessageCircle size={16} /> WhatsApp</a>
          </div> : <Button
            onClick={handlePayNow}
            disabled={!isFormValid() || finalAmount <= 0 || isProcessingPayment}
            data-testid="svc-calc-pay-now"
            className={`w-full min-h-[48px] h-12 text-base font-semibold rounded-xl transition-all ${
              isFormValid() && finalAmount > 0
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            <CreditCard size={18} className="mr-2" />
            {isProcessingPayment
              ? 'Opening Razorpay…'
              : (finalAmount > 0 ? `${config.cta} • ₹${finalAmount.toLocaleString()}` : config.cta)}
          </Button>}

          <Button
            onClick={handleSubmitEnquiry}
            disabled={!isEnquiryValid() || enquirySubmitting}
            data-testid="svc-calc-submit-enquiry"
            variant="outline"
            className={`w-full min-h-[44px] h-11 text-sm font-semibold rounded-xl transition-all ${
              isEnquiryValid()
                ? 'border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100'
                : 'border-gray-200 text-gray-400'
            }`}
          >
            <Send size={15} className="mr-2" />
            {enquirySubmitting ? 'Submitting…' : (isAreaOverLimit ? 'Request a Custom Quote' : 'Submit Enquiry')}
          </Button>

          <Button
            onClick={handleWhatsApp}
            disabled={!isFormValid() || isRedirecting}
            data-testid="svc-calc-whatsapp"
            variant="outline"
            className={`w-full min-h-[44px] h-11 text-sm font-semibold rounded-xl transition-all ${
              isFormValid()
                ? 'border-green-600 text-green-700 hover:bg-green-50'
                : 'border-gray-200 text-gray-400'
            }`}
          >
            <MessageCircle size={16} className="mr-2" />
            {isRedirecting ? 'Opening WhatsApp…' : 'Chat on WhatsApp Instead'}
          </Button>

          <button
            type="button"
            onClick={handleClear}
            data-testid="svc-calc-clear"
            className="w-full text-xs text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 py-1"
          >
            <RotateCcw size={12} /> Clear
          </button>
        </div>

        <p className="text-[11px] text-center text-gray-500 flex items-center justify-center gap-1 pt-1 border-t border-gray-100">
          <Shield size={11} className="text-green-600" />
          {config.note || 'Secure payment powered by Razorpay'}
        </p>
      </div>
    </div>
  );
}

ServiceCalculator.displayName = 'ServiceCalculator';
